import React, { createContext, useState, useContext, useMemo, useEffect } from 'react';
import { ThemeProvider as MuiThemeProvider } from '@mui/material/styles';
import { createAppTheme } from '../styles/theme';

// Create context
export const ThemeContext = createContext({
  themeMode: 'light',
  toggleThemeMode: () => {},
});

// Create theme context provider
export const ThemeProvider = ({ children }) => {
  // Try to get saved theme from localStorage
  const [themeMode, setThemeMode] = useState(() => {
    const savedTheme = localStorage.getItem('themeMode');
    // Check for saved preference or use system preference
    if (savedTheme) {
      return savedTheme;
    }
    // Check for system preference
    if (window.matchMedia && window.matchMedia('(prefers-color-scheme: dark)').matches) {
      return 'dark';
    }
    return 'light';
  });

  // Create the theme instance
  const theme = useMemo(() => createAppTheme(themeMode), [themeMode]);

  // Toggle theme mode function
  const toggleThemeMode = () => {
    setThemeMode((prevMode) => {
      const newMode = prevMode === 'light' ? 'dark' : 'light';
      localStorage.setItem('themeMode', newMode);
      return newMode;
    });
  };

  // Listen for system preference changes
  useEffect(() => {
    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
    
    const handleChange = (e) => {
      // Only update if user hasn't set a preference
      if (!localStorage.getItem('themeMode')) {
        setThemeMode(e.matches ? 'dark' : 'light');
      }
    };

    // Add listener for older browsers
    if (typeof mediaQuery.addEventListener === 'function') {
      mediaQuery.addEventListener('change', handleChange);
    } else {
      mediaQuery.addListener(handleChange);
    }

    return () => {
      // Clean up for older browsers
      if (typeof mediaQuery.removeEventListener === 'function') {
        mediaQuery.removeEventListener('change', handleChange);
      } else {
        mediaQuery.removeListener(handleChange);
      }
    };
  }, []);

  // Update body background based on theme
  useEffect(() => {
    document.body.style.backgroundColor = themeMode === 'light' ? '#FFFFFF' : '#121212';
    document.body.style.color = themeMode === 'light' ? '#111827' : '#F3F4F6';
  }, [themeMode]);

  // Create context value
  const contextValue = {
    themeMode,
    toggleThemeMode,
  };

  return (
    <ThemeContext.Provider value={contextValue}>
      <MuiThemeProvider theme={theme}>
        {children}
      </MuiThemeProvider>
    </ThemeContext.Provider>
  );
};

// Custom hook for using the theme context
export const useTheme = () => useContext(ThemeContext);